'use client';
import { FC, useEffect, useMemo, useState } from 'react';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { useTranslations } from 'next-intl';
import HeightLimitedExpander from 'components/height-limited-expander';
import {
  CatHeading,
  CatList,
  CatListItem,
  CatSegmentedControl,
  CatSegmentedControlItem,
  CatTab,
  CatTabPanel,
  CatTabs
} from 'components/blocks-components';
import { Conditional } from 'components/pcc-components';
import { setUnitOfMeasureCookie } from 'utils/uomCookie';
import { UOM, UOMINCH, UOMMETRIC } from 'constants/common';
import AnchorMenuMobileSectionHeader from '../anchor-menu-mobile/anchor-menu-mobile-section-header/anchor-menu-mobile-section-header';
import cx from 'classnames';
import styles from './anchor-menu-specifications.module.scss';
import { initialIndex, numberOfAttribuesToDisplay } from '../utils';

export interface AnchorMenuSpecificationsProps {
  data: {
    partNumber: string;
    attributes: Array<{ name: string; value: string; uom: string }>;
  };
  defaultUom: string;
  isMobile?: boolean;
  isPCC_PDP_headerUpdateMobileView?: boolean;
  isUpdatePDPHeaderMobileFlag?: boolean;
}

const AnchorMenuSpecifications: FC<AnchorMenuSpecificationsProps> = ({
  data,
  defaultUom,
  isMobile = false,
  isPCC_PDP_headerUpdateMobileView = false,
  isUpdatePDPHeaderMobileFlag = false
}) => {
  const [uomSelected, setUomSelected] = useState(defaultUom);
  const [attributes, setAttributes] = useState(data?.attributes ?? []);
  const t = useTranslations();
  const desktopTitle = replaceTokensWithComponents(
    t('PDP_SPECIFICATIONS', { '0': '{0}' }),
    data.partNumber
  );
  const noAtrributesMessage = (
    <p className="cat-u-margin-bottom-none cat-u-theme-typography-body">
      {t('PART_SPECIFICATION_INFORMATION_NOT_AVAILABLE')}
    </p>
  );

  const checkMetricAndUs = useMemo(() => {
    const setMetricAndUs = new Set();
    data?.attributes?.forEach((item) => {
      if (item.uom === UOM.metric || item.uom === UOM.us) {
        setMetricAndUs.add(item.uom);
      }
    });
    return setMetricAndUs.size > 1;
  }, [data?.attributes]);

  useEffect(() => {
    if (uomSelected) {
      const filteredAttributes = data?.attributes?.filter(
        (attribute) =>
          attribute.uom === uomSelected || attribute.uom === UOM.c62
      );
      setAttributes(filteredAttributes ?? []);
    }
  }, [data?.attributes, uomSelected]);

  const handleUomChange = (tabIndex: number) => {
    const uomValue = tabIndex === 0 ? UOM.us : UOM.metric;
    setUnitOfMeasureCookie(uomValue);
    setUomSelected(uomValue);
  };

  const renderUom = () => (
    <>
      <CatHeading variant="label-lg">{t('UOM')}</CatHeading>
      <CatSegmentedControl
        activeIndex={uomSelected === UOM.us ? 0 : 1}
        onBlSegmentItemChange={(e) => handleUomChange(e.detail.activeIndex)}
      >
        <CatSegmentedControlItem>{t(UOMINCH)}</CatSegmentedControlItem>
        <CatSegmentedControlItem>{t(UOMMETRIC)}</CatSegmentedControlItem>
      </CatSegmentedControl>
    </>
  );
  return (
    <div className="d-flex flex-column row-gap-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 gap-md-0">
        <Conditional
          test={isMobile && isUpdatePDPHeaderMobileFlag}
          fallback={<CatHeading variant="title" className='cat-u-theme-typography-title'>{desktopTitle}</CatHeading>}
        >
          <AnchorMenuMobileSectionHeader
            shouldShowButton={
              isPCC_PDP_headerUpdateMobileView &&
              attributes.length > numberOfAttribuesToDisplay
            }
            titleText={t('SPECIFICATIONS')}
          >
            <Conditional test={isPCC_PDP_headerUpdateMobileView}>
              <>
                <Conditional test={checkMetricAndUs}>
                  <div className={cx(styles['cat-uom-container'], 'mb-3')}>
                    {renderUom()}
                  </div>
                </Conditional>
                <div>
                  {attributes.map((attribute) => (
                    <div className=" mb-2 gap-1 d-flex" key={attribute.name}>
                      <CatHeading variant="label">{`${attribute.name}: `}</CatHeading>
                      <CatHeading variant="body-sm">
                        {attribute.value}
                      </CatHeading>
                    </div>
                  ))}
                </div>
              </>
            </Conditional>
          </AnchorMenuMobileSectionHeader>
        </Conditional>

        <Conditional test={!isEmpty(attributes) && checkMetricAndUs}>
          <Conditional
            test={isPCC_PDP_headerUpdateMobileView && isMobile}
            fallback={
              <div className="d-flex flex-column flex-md-row align-items-md-center gap-1 gap-md-3">
                <p className="cat-u-theme-typography-body-sm m-0">{t('UOM')}</p>
                <CatTabs
                  activeIndex={uomSelected === UOM.us ? 0 : 1}
                  variant="contrast"
                  onBlTabChange={(e) =>
                    handleUomChange(e.detail.activeTabIndex)
                  }
                >
                  <CatTab>{t(UOMINCH)}</CatTab>
                  <CatTabPanel slot="panel" className="d-none" />
                  <CatTab>{t(UOMMETRIC)}</CatTab>
                  <CatTabPanel slot="panel" className="d-none" />
                </CatTabs>
              </div>
            }
          >
            <div className={styles['cat-uom-container']}>{renderUom()}</div>
          </Conditional>
        </Conditional>
      </div>
      <Conditional test={!isEmpty(attributes)} fallback={noAtrributesMessage}>
        <div>
          <HeightLimitedExpander
            buttonSize="lg"
            id="specificationsExpander"
            lessButtonText={t('VIEW_LESS')}
            moreButtonText={t('VIEW_MORE_PRODUCTS')}
          >
            <CatList spacing="condensed" className={styles['cat-c-list']}>
              {attributes
                .slice(
                  initialIndex,
                  isPCC_PDP_headerUpdateMobileView && isMobile
                    ? numberOfAttribuesToDisplay
                    : attributes.length
                )
                .map((attribute) => (
                  <Conditional
                    key={attribute.name}
                    test={isPCC_PDP_headerUpdateMobileView}
                    fallback={
                      <CatListItem key={attribute.name} isStatic>
                        <p className="cat-u-margin-bottom-none cat-u-theme-typography-body">
                          <strong>{`${attribute.name}: `}</strong>
                          {attribute.value}
                        </p>
                      </CatListItem>
                    }
                  >
                    <div className=" mb-2 gap-1 d-flex">
                      <CatHeading variant="label">{`${attribute.name}: `}</CatHeading>
                      <CatHeading variant="body-sm">
                        {attribute.value}
                      </CatHeading>
                    </div>
                  </Conditional>
                ))}
            </CatList>
          </HeightLimitedExpander>
        </div>
      </Conditional>
    </div>
  );
};

export default AnchorMenuSpecifications;
